import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CharacterCreationDraftDnd5e,
  CharacterCreationEvaluationDnd5e,
  CharacterCreationInvalidationDnd5e,
} from "@/lib/core/schemas/character-creation";
import {
  evaluateCharacterCreation,
  evaluateStandaloneCharacterCreation,
} from "@/src/api/character-creation";

export type UseCharacterCreationEvaluationResult = {
  readonly evaluation: CharacterCreationEvaluationDnd5e | null;
  readonly invalidations: readonly CharacterCreationInvalidationDnd5e[];
  readonly error: Error | null;
  readonly isLoading: boolean;
  readonly refresh: () => Promise<void>;
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

export const useCharacterCreationEvaluation = (
  campaignId: string | null,
  draft: CharacterCreationDraftDnd5e | null
): UseCharacterCreationEvaluationResult => {
  const [evaluation, setEvaluation] =
    useState<CharacterCreationEvaluationDnd5e | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestSeq = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = requestSeq.current + 1;
    requestSeq.current = requestId;

    if (!campaignId || !draft) {
      setEvaluation(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const nextEvaluation = await evaluateCharacterCreation(campaignId, draft);
      if (requestSeq.current !== requestId) {
        return;
      }
      setEvaluation(nextEvaluation);
    } catch (caught) {
      if (requestSeq.current !== requestId) {
        return;
      }
      setError(toError(caught));
    } finally {
      if (requestSeq.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [campaignId, draft]);

  useEffect(() => {
    refresh().catch(() => {
      // refresh records errors in state; keep effects free of unhandled promises.
    });
  }, [refresh]);

  return {
    evaluation,
    invalidations: evaluation?.invalidations ?? [],
    error,
    isLoading,
    refresh,
  };
};

export const useStandaloneCharacterCreationEvaluation = (
  enabled: boolean,
  draft: CharacterCreationDraftDnd5e | null
): UseCharacterCreationEvaluationResult => {
  const [evaluation, setEvaluation] =
    useState<CharacterCreationEvaluationDnd5e | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestSeq = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = requestSeq.current + 1;
    requestSeq.current = requestId;

    if (!enabled || !draft) {
      setEvaluation(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const nextEvaluation = await evaluateStandaloneCharacterCreation(draft);
      if (requestSeq.current !== requestId) {
        return;
      }
      setEvaluation(nextEvaluation);
    } catch (caught) {
      if (requestSeq.current !== requestId) {
        return;
      }
      setError(toError(caught));
    } finally {
      if (requestSeq.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [enabled, draft]);

  useEffect(() => {
    refresh().catch(() => {
      // refresh records errors in state; keep effects free of unhandled promises.
    });
  }, [refresh]);

  return {
    evaluation,
    invalidations: evaluation?.invalidations ?? [],
    error,
    isLoading,
    refresh,
  };
};
